import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, map, startWith } from 'rxjs';
import { Collection } from 'src/app/models/collection.interface';

export interface CloneDialogData {
  collections: Collection[];
  selectedDocuments?: unknown[];
}

export interface CloneDialogResult {
  selectedCollectionId: string;
  selectedCollection: Collection;
}

@Component({
  selector: 'app-clone-documents-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule
  ],
  template: `
    <div class="clone-dialog-header">
      <h1 mat-dialog-title>Clone Documents</h1>
      <button mat-icon-button (click)="onNoClick()" class="close-button" aria-label="Close dialog">
        <mat-icon>close</mat-icon>
      </button>
    </div>
    <div mat-dialog-content class="clone-dialog-content">
      <p class="dialog-description">Select a destination collection:</p>
      <mat-form-field class="collection-search-field" appearance="outline">
        <mat-label>Search Collections</mat-label>
        <input
          type="text"
          placeholder="Type to search collections..."
          aria-label="Collection search"
          matInput
          [formControl]="collectionControl"
          [matAutocomplete]="auto" />
        <mat-autocomplete
          #auto="matAutocomplete"
          [displayWith]="displayCollection"
          (optionSelected)="onCollectionSelected($event)">
          <mat-option
            *ngFor="let collection of filteredCollections | async"
            [value]="collection"
            class="collection-option">
            <span class="collection-name">{{ collection.name }}</span>
            <span class="collection-description" *ngIf="collection.description">
              {{ collection.description }}
            </span>
          </mat-option>
          <mat-option *ngIf="(filteredCollections | async)?.length === 0" disabled class="no-results-option">
            No collections found
          </mat-option>
        </mat-autocomplete>
      </mat-form-field>
    </div>
    <div mat-dialog-actions class="clone-dialog-actions">
      <button mat-button (click)="onNoClick()" class="cancel-button" [disabled]="isProcessing">Cancel</button>
      <button
        mat-raised-button
        color="primary"
        [disabled]="!selectedCollection || isProcessing"
        (click)="onCloneClick()"
        class="clone-button">
        <mat-spinner *ngIf="isProcessing" diameter="20" class="button-spinner"></mat-spinner>
        <span *ngIf="!isProcessing">Clone Documents</span>
        <span *ngIf="isProcessing">Cloning...</span>
      </button>
    </div>
  `,
  styles: [
    `
      .clone-dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;
      }

      .close-button {
        margin-left: auto;
      }

      .clone-dialog-content {
        min-width: 400px;
        max-width: 500px;
      }

      .dialog-description {
        margin-bottom: 16px;
        color: rgba(0, 0, 0, 0.6);
      }

      .collection-search-field {
        width: 100%;
      }

      .collection-option {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding: 12px 16px;
        line-height: 1.4;
      }

      .collection-name {
        font-weight: 500;
        margin-bottom: 4px;
      }

      .collection-description {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.6);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }

      .no-results-option {
        color: rgba(0, 0, 0, 0.6);
        font-style: italic;
      }

      .clone-dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        margin-top: 24px;
      }

      .cancel-button {
        color: rgba(0, 0, 0, 0.6);
      }

      .clone-button {
        min-width: 120px;
        position: relative;
      }

      .button-spinner {
        margin-right: 8px;
      }
    `
  ]
})
export class CloneDocumentsDialogComponent implements OnInit {
  collectionControl = new FormControl<string | Collection>('');
  filteredCollections: Observable<Collection[]>;
  selectedCollection: Collection | null = null;
  availableCollections: Collection[] = [];
  isProcessing = false;
  errorMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CloneDocumentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CloneDialogData
  ) {
    // Filter out hidden collections and sort alphabetically
    this.availableCollections = this.filterAndSortCollections(this.data.collections);

    // Set up filtered collections observable
    this.filteredCollections = this.collectionControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterCollections(value))
    );
  }

  ngOnInit(): void {
    // Focus on the input field when dialog opens
    setTimeout(() => {
      const input = document.querySelector('.collection-search-field input') as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  /**
   * Filter out hidden collections and sort alphabetically (A → Z)
   * Requirements: 3.1, 3.3, 7.1, 7.2
   */
  private filterAndSortCollections(collections: Collection[]): Collection[] {
    return collections
      .filter(collection => !collection.is_hidden) // Exclude hidden collections
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })); // Alphabetical sort
  }

  /**
   * Filter collections based on search term
   * Requirements: 2.1, 2.2, 2.3
   */
  private filterCollections(value: string | Collection | null): Collection[] {
    if (!value) {
      return this.availableCollections;
    }

    const searchTerm = typeof value === 'string' ? value.toLowerCase() : '';
    if (!searchTerm) {
      return this.availableCollections;
    }

    return this.availableCollections.filter(
      collection =>
        collection.name.toLowerCase().includes(searchTerm) ||
        (collection.description && collection.description.toLowerCase().includes(searchTerm))
    );
  }

  /**
   * Display function for autocomplete
   * Requirements: 2.1
   */
  displayCollection(collection: Collection): string {
    return collection ? collection.name : '';
  }

  /**
   * Handle collection selection from autocomplete
   * Requirements: 3.4
   */
  onCollectionSelected(event: { option: { value: Collection } }): void {
    this.selectedCollection = event.option.value;
  }

  /**
   * Handle clone button click
   * Requirements: 4.3
   */
  onCloneClick(): void {
    if (!this.selectedCollection) {
      console.warn('No collection selected');
      return;
    }

    if (!this.data.selectedDocuments || this.data.selectedDocuments.length === 0) {
      console.warn('No documents selected for cloning');
      return;
    }

    // Validate collection is still available (not hidden)
    if (this.selectedCollection.is_hidden) {
      console.warn('Selected collection is no longer available');
      return;
    }

    this.isProcessing = true;
    this.errorMessage = null;

    const result: CloneDialogResult = {
      selectedCollectionId: this.selectedCollection.collection_id,
      selectedCollection: this.selectedCollection
    };

    // Close dialog with result - the parent component will handle the actual cloning
    this.dialogRef.close(result);
  }

  /**
   * Handle cancel/close actions
   * Requirements: 4.1, 4.2, 4.4
   */
  onNoClick(): void {
    if (this.isProcessing) {
      // Prevent closing during processing
      return;
    }
    this.dialogRef.close();
  }
}
